import React from 'react';

import Skeleton from 'react-loading-skeleton';

import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import BaseTile from 'CommonComponents/BaseTile';

import BillDollarIcon from 'Assets/icons/bill-dollar.svg';
import DollarIcon from 'Assets/icons/dollar.svg';

/**
 * IB-875 — Summary tiles above the R&E tables.
 *
 * Venue: Revenue total, Expenses total, Net total, Commitment (a two-figure
 * card splitting committed / uncommitted). Event: Revenue total, Expenditure
 * total, Net total, Commitment — both faces of the R&E table now expose the
 * committed / uncommitted split.
 *
 * The design system has no amber token; "uncommitted" therefore uses the same
 * muted charcoal used for the uncommitted sub-rows, and "committed" uses green.
 */
export default function RESummaryCards({
    context,
    revenues = null,
    expenses = null,
    loadingRevenues = false,
    loadingExpenses = false,
    totals = null,
    committedTotals = null,
    loading = false,
}) {
    if (context === 'event') {
        const revenueTotal = totals ? Number(totals.revenue ?? 0) : null;
        const expenditureTotal = totals ? Number(totals.expenditure ?? 0) : null;
        const netTotal = totals ? revenueTotal - expenditureTotal : null;

        return (
            <div className="tw-grid tw-grid-cols-4 tw-gap-x-3">
                <BaseTile
                    loading={loading}
                    title={$t('Revenue total')}
                    value={!loading && totals ? formatToFixedMoneyWithLocale(revenueTotal) : null}
                    icon={<BillDollarIcon className="tw-w-8 tw-h-8" />}
                />
                <BaseTile
                    loading={loading}
                    title={$t('Expenditure total')}
                    value={!loading && totals ? formatToFixedMoneyWithLocale(expenditureTotal) : null}
                    icon={<DollarIcon className="tw-w-8 tw-h-8" />}
                />
                <BaseTile
                    loading={loading}
                    title={$t('Net total')}
                    value={!loading && totals ? formatToFixedMoneyWithLocale(netTotal) : null}
                    icon={<BillDollarIcon className="tw-w-8 tw-h-8" />}
                />
                <CommitmentTile loading={loading} committedTotals={committedTotals} />
            </div>
        );
    }

    const revenueTotal = revenues ? Number(revenues.totals?.total ?? 0) : null;
    const expensesTotal = expenses ? Number(expenses.totals?.total ?? 0) : null;
    const netLoading = loadingRevenues || loadingExpenses;

    return (
        <div className="tw-grid tw-grid-cols-4 tw-gap-x-3">
            <BaseTile
                loading={loadingRevenues}
                title={$t('Revenue total')}
                value={!loadingRevenues && revenues ? formatToFixedMoneyWithLocale(revenueTotal) : null}
                icon={<BillDollarIcon className="tw-w-8 tw-h-8" />}
            />
            <BaseTile
                loading={loadingExpenses}
                title={$t('Expenses total')}
                value={!loadingExpenses && expenses ? formatToFixedMoneyWithLocale(expensesTotal) : null}
                icon={<DollarIcon className="tw-w-8 tw-h-8" />}
            />
            <BaseTile
                loading={netLoading}
                title={$t('Net total')}
                value={!netLoading && revenues && expenses ? formatToFixedMoneyWithLocale(revenueTotal - expensesTotal) : null}
                icon={<BillDollarIcon className="tw-w-8 tw-h-8" />}
            />
            <CommitmentTile loading={loadingExpenses} committedTotals={expenses?.committed_totals ?? null} />
        </div>
    );
}

function CommitmentTile({ loading, committedTotals }) {
    return (
        <div className="tw-px-4 tw-py-3 tw-rounded-lg tw-border-2 tw-border-grey">
            {!loading && committedTotals ? (
                <div>
                    <div className="tw-text-sm tw-text-blue-75 tw-font-medium tw-mb-2">{$t('Commitment')}</div>
                    <div className="tw-flex tw-items-end tw-gap-x-4">
                        <div>
                            <div className="tw-text-xs tw-text-charcoal-50">{$t('Committed')}</div>
                            <div className="tw-text-xl tw-text-green-500 tw-font-semibold">{formatToFixedMoneyWithLocale(committedTotals.committed)}</div>
                        </div>
                        <div>
                            <div className="tw-text-xs tw-text-charcoal-50">{$t('Uncommitted')}</div>
                            <div className="tw-text-xl tw-text-charcoal-75 tw-font-semibold">{formatToFixedMoneyWithLocale(committedTotals.uncommitted)}</div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="tw-space-y-2 tw-h-full">
                    <Skeleton width="100px" height={20} />
                    <Skeleton width="180px" height={28} />
                </div>
            )}
        </div>
    );
}
